import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import QRCode from 'qrcode';

interface AppQrCodeProps {
  /** App name shown on the exported sheet, e.g. "SDICS Field". */
  name: string;
  /** Permanent download link; the QR code must keep working across releases. */
  url: string;
  /** One line under the name on the exported sheet. */
  audience: string;
  accent: string;
}

const STEPS = [
  '1. Open the phone camera or Google Lens and point it at the code.',
  '2. Tap the link that appears. The app downloads straight away.',
  '3. Open the downloaded file and tap Install.',
  '   First time only: allow "Install unknown apps" for Chrome.',
];

// Level Q survives creased or smudged printouts; 2048px stays sharp at A4.
const toDataUrl = (url: string, width: number) =>
  QRCode.toDataURL(url, { errorCorrectionLevel: 'Q', margin: 4, width, color: { dark: '#000000', light: '#ffffff' } });

const fileSlug = (name: string) => name.replace(/\s+/g, '-');

/** Scan-to-download QR code for an app, exportable as PNG or a printable A4 PDF. */
export const AppQrCode: React.FC<AppQrCodeProps> = ({ name, url, audience, accent }) => {
  const [preview, setPreview] = useState('');

  useEffect(() => {
    toDataUrl(url, 360).then(setPreview).catch(() => setPreview(''));
  }, [url]);

  const savePng = async () => {
    const a = document.createElement('a');
    a.href = await toDataUrl(url, 2048);
    a.download = `${fileSlug(name)}-QR.png`;
    a.click();
  };

  const savePdf = async () => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const W = 210;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(36);
    doc.text(name, W / 2, 30, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(15);
    doc.setTextColor(60);
    doc.text(audience, W / 2, 41, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(19);
    doc.setTextColor(0);
    doc.text('Scan to download the Android app', W / 2, 54, { align: 'center' });
    const side = 140;
    // 1200px is ~220 dpi at 14 cm; FAST compression keeps the PDF small enough to share.
    doc.addImage(await toDataUrl(url, 1200), 'PNG', (W - side) / 2, 62, side, side, undefined, 'FAST');
    doc.setFontSize(14);
    doc.text(url, W / 2, 212, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(13);
    STEPS.forEach((step, i) => doc.text(step, 30, 230 + i * 9));
    doc.save(`${fileSlug(name)}-QR.pdf`);
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', p: 2, backgroundColor: '#0f1419', borderRadius: 2, mb: 3 }}>
      <Box sx={{ width: 160, height: 160, backgroundColor: '#ffffff', borderRadius: 1, flexShrink: 0 }}>
        {preview && <img src={preview} alt={`QR code to download ${name}`} width={160} height={160} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="body2" sx={{ color: '#8b95a5', fontWeight: 600, mb: 0.5 }}>
          Scan to download
        </Typography>
        <Typography variant="body2" sx={{ color: '#d1d5db', mb: 1.5 }}>
          Phones scan this with the camera or Google Lens and the app downloads directly. The link
          always serves the newest version, so printed copies never go out of date.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={savePdf}
            sx={{ color: accent, borderColor: accent }}>
            Export PDF
          </Button>
          <Button size="small" variant="outlined" startIcon={<ImageIcon />} onClick={savePng}
            sx={{ color: accent, borderColor: accent }}>
            Save PNG
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
